/**
 * External agents come off the wire in a different shape than the form edits them in, and
 * conflating the two is what made this screen render `[object Object]` for a phone number and
 * carry an `as unknown as` cast in the list to paper over it.
 *
 * The API returns the Eloquent model with its relations: `phone` is a row, `skills` carry
 * their value on a pivot. The form edits a flat agent: a phone string, a list of department
 * ids, a list of {id, value}. `fromResponse` and `toPayload` are the only two places that
 * know both.
 */

import { IAgentCustomSkill } from "../../models/AgentSkills/AgentCustomSkill";

/** Activity flags, in the order the form renders them. Mirrors ExternalAgentService::ACTIVITY_FIELDS. */
export const ACTIVITY_FIELDS = [
  "booking",
  "support",
  "complains",
  "membership",
  "voicemail",
  "marketing",
  "dsr",
  "reception",
] as const;

/** KPI / grouping flags. Mirrors ExternalAgentService::KPI_FIELDS. */
export const KPI_FIELDS = [
  "adl",
  "best_booker",
  "best_sel",
  "cancelation_ratio",
  "professional",
  "amateur",
  "beginner",
  "group_d",
  "group_e",
  "group_f",
] as const;

export type ActivityField = typeof ACTIVITY_FIELDS[number];
export type KpiField = typeof KPI_FIELDS[number];
export type FlagField = ActivityField | KpiField;

export const FLAG_FIELDS: ReadonlyArray<FlagField> = [
  ...ACTIVITY_FIELDS,
  ...KPI_FIELDS,
];

/** Every boolean column, all present, all false. */
export type ExternalAgentFlags = { [K in FlagField]: boolean };

/** The agent as the form edits it. */
export interface IExternalAgent extends ExternalAgentFlags {
  id?: number;
  /** Twilio worker id. Null until the worker exists — see the sid nullability migration. */
  sid: string | null;
  name: string;
  phone: string;
  team_id: number | null;
  departments: Array<number>;
  skills: Array<IAgentCustomSkill>;
  /** null means "unknown": no worker yet, or TaskRouter could not be read. */
  available: boolean | null;
}

/**
 * A department as the picker needs it.
 *
 * DepartmentsActions.get() returns raw JSON, so these are lowercase-keyed plain objects — not
 * the `Department` class from src/models, whose accessors are PascalCase. The form typed them
 * as that class and passed them to a component expecting the interface; it worked only
 * because TypeScript's `private` is erased at runtime, and would have broken silently the
 * moment either side started mapping its payload.
 */
export interface IDepartmentOption {
  id: number;
  name: string;
}

interface IRelatedPhone {
  id: number;
  phone: string;
}

interface IRelatedNamed {
  id: number;
  name: string;
}

interface IRelatedSkill {
  id: number;
  key: string;
  type_id: number;
  pivot?: { value: string | null };
}

/** The agent as the API returns it. */
export interface IExternalAgentResponse extends Partial<ExternalAgentFlags> {
  id: number;
  sid: string | null;
  name: string | null;
  team_id: number | null;
  phone?: IRelatedPhone | null;
  team?: IRelatedNamed | null;
  departments?: Array<IRelatedNamed>;
  skills?: Array<IRelatedSkill>;
  available?: boolean | null;
}

/** Envelope used by the create and update endpoints. The list endpoint returns a bare array. */
export interface IExternalAgentEnvelope {
  success: boolean;
  message?: string;
  twilio_synced?: boolean;
  data: IExternalAgentResponse;
}

/**
 * A blank agent with every field defined.
 *
 * The form used to start from `{} as IExternalAgent`, so every input rendered with
 * `value={undefined}` and every checkbox with `checked={undefined}` — uncontrolled on the
 * first render, controlled on the second, which is both a React warning and the reason
 * activities and KPIs appeared unchecked no matter what the record said.
 */
export function emptyExternalAgent(): IExternalAgent {
  const flags = {} as ExternalAgentFlags;
  FLAG_FIELDS.forEach((field) => {
    flags[field] = false;
  });

  return {
    ...flags,
    sid: null,
    name: "",
    phone: "",
    team_id: null,
    departments: [],
    skills: [],
    available: null,
  };
}

export function fromResponse(row: IExternalAgentResponse): IExternalAgent {
  const agent = emptyExternalAgent();

  FLAG_FIELDS.forEach((field) => {
    agent[field] = row[field] === true;
  });

  return {
    ...agent,
    id: row.id,
    sid: row.sid,
    name: row.name || "",
    phone: row.phone ? row.phone.phone : "",
    // team_id can arrive as a string from MySQL depending on the driver's casting.
    team_id: row.team_id === null || row.team_id === undefined ? null : Number(row.team_id),
    departments: (row.departments || []).map((d) => d.id),
    skills: (row.skills || []).map((s) => ({
      id: s.id,
      value: s.pivot && s.pivot.value !== null ? s.pivot.value : "",
    })),
    available: row.available === undefined ? null : row.available,
  };
}

/**
 * The write payload.
 *
 * `sid` and `available` are deliberately not sent: the worker id is the server's to assign,
 * and availability is changed through the toggle endpoint.
 */
export function toPayload(agent: IExternalAgent): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: agent.name.trim(),
    phone: agent.phone.trim(),
    departments: agent.departments,
    skills: agent.skills.map((s) => ({ id: s.id, value: s.value })),
  };

  FLAG_FIELDS.forEach((field) => {
    payload[field] = agent[field];
  });

  // Sent even when null. An absent key would leave the existing team in place, so clearing
  // "No Team" has to be expressed explicitly; the server's `sometimes|integer|exists` rules
  // skip a null, and it maps the null onto the column.
  payload.team_id = agent.team_id;

  return payload;
}

export interface ITaskChannel {
  sid: string;
  name: string;
  capacity: number;
  available: boolean;
}
